import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Filters } from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { ProgressBarOverlayComponent } from 'src/app/components/progress-bar-overlay/progress-bar-overlay.component';
import { PromptAI, PromptAIService } from 'src/app/services/promptAI/prompt-ai.service';
import {
  TIPO_MODULO,
  TIPO_MODULO_LABEL,
  TipoModulo,
  UtilityService,
} from 'src/app/services/utility/utility.service';

interface Placeholders {
  chiave: string;
  label: string;
  tipoModulo: TipoModulo[];
}
@Component({
  selector: 'app-cfg-general-prompt',
  standalone: false,

  templateUrl: './cfg-general-prompt.component.html',
  styleUrl: './cfg-general-prompt.component.scss',
})
export class CfgGeneralPromptComponent implements AfterViewInit {
  @ViewChild(ProgressBarOverlayComponent) progressBarOverlay!: ProgressBarOverlayComponent;

  public placeholders: Placeholders[] = [];

  form = new FormGroup({
    promptAI: new FormArray<FormGroup>([]),
  });

  get promptAIArray(): FormArray<FormGroup> {
    return this.form.get('promptAI') as FormArray<FormGroup>;
  }

  constructor(
    private readonly _utility: UtilityService,
    private readonly _promptAIService: PromptAIService,
    private el: ElementRef,
  ) {}

  addPromptAIToFormArray(prompt: PromptAI): void {
    const group = new FormGroup({
      id: new FormControl(prompt.id),
      dataUltimaModifica: new FormControl(prompt.dataUltimaModifica),
      prompt: new FormControl(prompt.prompt),
      idElemento: new FormControl(prompt.idElemento),
      tipo: new FormControl<TipoModulo>(prompt.tipo as TipoModulo),
      generale: new FormControl(true),
    });

    this.promptAIArray.push(group);
  }

  ngAfterViewInit(): void {
    this._init();

    // timeout per attendere che Angular Material
    // abbia renderizzato il contenuto dei tab e popolato i form
    // cosi da avere una textarea allungata in base al contenuto già in prima visualizzazione
    setTimeout(() => {
      const textareas = this.el.nativeElement.querySelectorAll('textarea.mat-mdc-input-element');
      textareas.forEach((txt: HTMLTextAreaElement) => this.resizeTextarea(txt));
    }, 200);
  }

  getlabelModulo(tipoModulo: TipoModulo) {
    return TIPO_MODULO_LABEL[tipoModulo];
  }

  private async _init() {
    this.buildPlaceholders();

    const promptGenerali = await this._getPromptGenerali();

    TIPO_MODULO.forEach((mod) => {
      // Se promptGenerali è un array, cerca l'esistente, altrimenti undefined
      const promptEsistente = Array.isArray(promptGenerali)
        ? promptGenerali.find((pr) => pr.tipo === mod)
        : undefined;

      const prompt: PromptAI = promptEsistente ?? {
        id: '',
        dataUltimaModifica: '',
        prompt: '',
        idElemento: '',
        tipo: mod,
        generale: true,
      };

      this.addPromptAIToFormArray(prompt);
    });
  }

  /**
   * Salvataggio del prompt sul modulo passatto
   * @param group
   */
  async salva(group: FormGroup<any>) {
    const promptAI: PromptAI = group.getRawValue();

    this.progressBarOverlay.show('Salvataggio prompt ' + promptAI.tipo + '...');

    try {
      const result = await this._promptAIService.salva(promptAI);

      console.log(group.getRawValue());
      if (result) {
        Object.keys(result).forEach((key) => {
          group.get(key)?.setValue((result as any)[key]);
        });
      }
      console.log(group.getRawValue());
    } catch (error) {
      console.error(error);
    } finally {
      this.progressBarOverlay.hide();
    }
  }

  async copiaPlaceholder(placeholder: string) {
    await navigator.clipboard.writeText('{{' + placeholder + '}}');

    this._utility.openSnackBar('Placeholder copiato!', '', {
      duration: 3000,
      panelClass: ['success-snackbar'],
    });
  }

  private async _getPromptGenerali(): Promise<PromptAI[] | false> {
    const filtri: Filters[] = [
      {
        chiave: 'generale',
        operator: 'is',
        values: [true],
      },
    ];

    this.progressBarOverlay.show('Caricamento prompt generali...');
    try {
      const result = await firstValueFrom(
        this._promptAIService.listaPrompt(0, 1000, '', filtri, []),
      );
      return result;
    } catch (error: any) {
      this._utility.openSnackBar(error?.error?.message ?? 'Errore nel reperimento dei prompt', '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });
      return false;
    } finally {
      this.progressBarOverlay.hide();
    }
  }

  buildPlaceholders() {
    this.placeholders.push(
      {
        label: 'Anno modulo',
        chiave: 'ANNO_MODULO',
        tipoModulo: [
          'MATERIALITA',
          'SINTESI',
          'BILANCIO',
          'POLICY',
          'PIANO',
          'DOPPIA_MATERIALITA',
          'REPORT_VSME' as TipoModulo,
        ],
      },
      {
        label: 'Tipo Azienda',
        chiave: 'TIPO_AZIENDA',
        tipoModulo: ['REPORT_VSME' as TipoModulo],
      },
      {
        label: 'Tipo VSME',
        chiave: 'TIPO_VSME',
        tipoModulo: ['REPORT_VSME' as TipoModulo],
      },
    );
  }

  // 1. Metodo generico per il ridimensionamento
  private resizeTextarea(textarea: HTMLTextAreaElement) {
    const viewport =
      this.el.nativeElement.closest('ng-scrollbar')?.querySelector('.ng-scroll-viewport') ||
      document.querySelector('.ng-scroll-viewport');

    if (viewport) {
      const currentScroll = viewport.scrollTop;

      // Forza il ricalcolo dell'altezza
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;

      // Ripristina lo scroll per evitare il glitch tremante
      viewport.scrollTop = currentScroll;
      requestAnimationFrame(() => {
        viewport.scrollTop = currentScroll;
      });
    }
  }

  /**
   * Metodo per l'evento (input) nell'HTML
   * ricalcola l'alezza della text area in base al contenuto
   * @param event
   */
  adjustHeight(event: Event) {
    this.resizeTextarea(event.target as HTMLTextAreaElement);
  }

  /**
   * Quando cambi tab, diamo il tempo al nuovo tab di renderizzare la textarea e la ridimensioniamo
   */
  onTabChange() {
    setTimeout(() => {
      const activeTextarea = this.el.nativeElement.querySelector(
        '.mat-mdc-tab-body-active textarea.mat-mdc-input-element',
      );
      if (activeTextarea) {
        this.resizeTextarea(activeTextarea as HTMLTextAreaElement);
      }
    }, 100);
  }
}
